import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { auth, db, googleProvider, functions } from '../services/firebase';
import { requestPasswordReset } from '../services/passwordResetService';
import { DEPLOYMENT_CONFIG, ROLE_PROVIDERS } from '../config/deploymentConfig';

// Create Auth Context
const AuthContext = createContext();

const VALID_APP_ROLES = ['ADMIN', 'INSPECTOR', 'SUPERVISOR'];

const normalizeEmail = (email) => `${email || ''}`.trim().toLowerCase();

// Auth Provider Component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Safety timeout to prevent infinite loading
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ Safety timeout: Setting loading to false after 15 seconds');
        setLoading(false);
      }
    }, 15000);

    return () => clearTimeout(safetyTimeout);
  }, [loading]);

  // Resolve the signed-in user's app role with retry logic.
  const determineUserRole = useCallback(async (user, maxRetries = 3) => {
    if (!user || !user.email) {
      return 'DENIED';
    }

    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        process.env.REACT_APP_ENVIRONMENT === 'development' && console.log(`🔐 Authenticating user: ${user.email} (attempt ${attempt}/${maxRetries})`);
        
        let result;

        if (process.env.REACT_APP_SET_USER_ROLE_URL) {
          // Optional legacy HTTP endpoint path for deployments that expose it.
          const idToken = await auth.currentUser.getIdToken();
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);

          const response = await fetch(process.env.REACT_APP_SET_USER_ROLE_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({}),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          result = await response.json();
        } else if (process.env.REACT_APP_USE_CALLABLE_ROLE_RESOLVER === 'true') {
          const setUserRoleFn = httpsCallable(functions, 'setUserRole');
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new DOMException('Role request timed out', 'AbortError')), 10000);
          });
          const callableResult = await Promise.race([
            setUserRoleFn({}),
            timeoutPromise
          ]);

          result = callableResult.data || {};
        } else {
          const requestRef = doc(db, 'roleRequests', `${auth.currentUser.uid}-${Date.now()}`);
          await setDoc(requestRef, {
            uid: auth.currentUser.uid,
            email: user.email,
            status: 'pending',
            requestedAt: serverTimestamp()
          });

          result = await new Promise((resolve, reject) => {
            let unsubscribe = () => {};
            const timeoutId = setTimeout(() => {
              unsubscribe();
              reject(new DOMException('Role request timed out', 'AbortError'));
            }, 15000);

            unsubscribe = onSnapshot(requestRef, (snapshot) => {
              const data = snapshot.data() || {};
              if (data.status === 'completed') {
                clearTimeout(timeoutId);
                unsubscribe();
                resolve(data);
              } else if (data.status === 'error') {
                clearTimeout(timeoutId);
                unsubscribe();
                reject(new Error(data.error || 'Role request failed'));
              }
            }, (snapshotError) => {
              clearTimeout(timeoutId);
              unsubscribe();
              reject(snapshotError);
            });
          });
        }

        process.env.REACT_APP_ENVIRONMENT === 'development' && console.log(`✅ Authentication successful: ${user.email} → ${result.role}`);

        // Custom claims set by the function are not visible to Firestore rules
        // until the client refreshes its ID token.
        await auth.currentUser.getIdToken(true);
        
        return result.role || 'DENIED';
      } catch (error) {
        lastError = error;
        if (error.name === 'AbortError') {
          console.error(`⏱️ Authentication attempt ${attempt}/${maxRetries} timed out`);
        } else {
          console.error(`❌ Authentication attempt ${attempt}/${maxRetries} failed:`, error.message);
        }
        
        // Wait before retry (exponential backoff: 1s, 2s, 4s)
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed - fail secure
    console.error('🚫 Authentication failed. Access denied.');
    throw new Error(`Authentication failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
  }, []);

  const getAppManagedRoleFromFirestore = useCallback(async (user) => {
    const email = normalizeEmail(user?.email);
    if (!email) {
      return 'DENIED';
    }

    const firstAdminEmail = normalizeEmail(DEPLOYMENT_CONFIG.appUsers.firstAdminEmail);
    if (firstAdminEmail && email === firstAdminEmail) {
      return 'ADMIN';
    }

    const appUsersCollection = DEPLOYMENT_CONFIG.appUsers.collection || 'appUsers';
    const appUserDoc = await getDoc(doc(db, appUsersCollection, email));
    if (!appUserDoc.exists()) {
      return 'DENIED';
    }

    const appUser = appUserDoc.data() || {};
    if (`${appUser.status || 'active'}`.toLowerCase() !== 'active') {
      return 'DENIED';
    }

    const role = `${appUser.role || ''}`.trim().toUpperCase();
    return VALID_APP_ROLES.includes(role) ? role : 'DENIED';
  }, []);

  const refreshRoleClaimsInBackground = useCallback(async (user) => {
    try {
      const role = await determineUserRole(user, 1);
      if (role) {
        setUserRole(role);
      }
    } catch (error) {
      console.warn('Background role claim refresh failed:', error.message);
    }
  }, [determineUserRole]);

  // Sign in with Google OAuth with fallback mechanism
  const signInWithGoogle = async (useRedirect = false) => {
    try {
      setError(null);
      setLoading(true);
      
      if (useRedirect) {
        // Use redirect for mobile devices (better UX)
        await signInWithRedirect(auth, googleProvider);
      } else {
        // Use popup for desktop (faster)
        const result = await signInWithPopup(auth, googleProvider);
        process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('✅ Google sign-in successful:', result.user.email);
      }
    } catch (error) {
      console.error('❌ Sign-in error:', error.message);
      
      // Try fallback method if redirect fails
      if (useRedirect && error.code !== 'auth/cancelled-popup-request') {
        try {
          const result = await signInWithPopup(auth, googleProvider);
          process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('✅ Sign-in successful:', result.user.email);
          return;
        } catch (popupError) {
          setError(`Authentication failed: ${popupError.message}`);
        }
      } else {
        setError(`Sign-in failed: ${error.message}`);
      }
      setLoading(false);
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (error) {
      console.error('❌ Email sign-in error:', error.message);
      setError(`Sign-in failed: ${error.message}`);
      setLoading(false);
      throw error;
    }
  };

  const sendPasswordReset = async (email) => {
    try {
      setError(null);
      await requestPasswordReset(email);
    } catch (error) {
      console.error('❌ Password reset error:', error.message);
      setError(`Password reset failed: ${error.message}`);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserRole(null);
    } catch (error) {
      console.error('❌ Sign out error:', error);
      setError(error.message);
    }
  };

  // Check for redirect result on app load
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('✅ Google sign-in successful:', result.user.email);
          setError(null); // Clear any existing errors
        }
      } catch (error) {
        console.error('❌ Redirect error:', error.message);
        setError(`Redirect error: ${error.message}`);
      }
    };

    checkRedirectResult();
  }, []);

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('🔄 Auth state changed:', user ? user.email : 'No user');
      setUser(user);
      
      if (user) {
        try {
          setError('Determining access permissions...'); // Show loading message

          if (DEPLOYMENT_CONFIG.providers.role === ROLE_PROVIDERS.APP_MANAGED) {
            const role = await getAppManagedRoleFromFirestore(user);
            setUserRole(role);
            setError(null);
            setLoading(false);
            refreshRoleClaimsInBackground(user);
            return;
          }

          const role = await determineUserRole(user);
          setUserRole(role);
          setError(null); // Clear errors on successful auth
        } catch (error) {
          console.error('❌ Role determination failed:', error.message);
          setUserRole('DENIED');
          setError(`Access denied: ${error.message}. Please contact your system administrator if this persists.`);
        } finally {
          // Always clear loading state, even if role determination fails
          setLoading(false);
        }
      } else {
        setUserRole(null);
        setError(null); // Clear errors when user logs out
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [determineUserRole, getAppManagedRoleFromFirestore, refreshRoleClaimsInBackground]);

  // Context value
  const value = {
    user,
    userRole,
    loading,
    error,
    signInWithGoogle,
    signInWithEmail,
    sendPasswordReset,
    signOut,
    // Helper functions
    isAdmin: userRole === 'ADMIN',
    isInspector: userRole === 'INSPECTOR' || userRole === 'ADMIN',
    isSupervisor: userRole === 'SUPERVISOR' || userRole === 'ADMIN',
    hasAccess: userRole && userRole !== 'DENIED'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;
